import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Box,
  Checkbox,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudIcon from '@mui/icons-material/Cloud';
import PersonIcon from '@mui/icons-material/Person';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import dayjs, { Dayjs } from 'dayjs';
import {
  CompareToControl,
  DurationSelector,
  FilterDrawer,
  getAppliedFilterCount,
} from '../../../../../shared/components';
import type { DateRange, FilterState } from '../../../../../shared/components';
import { useClientScope } from '../../../../../shared/contexts';
import type { ReportFilters } from '../api/types';
import type { ReportTemplate } from '../utils/generateReport';

interface GenerateWizardProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (input: {
    templates: ReportTemplate[];
    filters: ReportFilters;
    periodLabel: string;
    /** Client the report is scoped to (null = all clients, central only). */
    clientId: string | null;
  }) => void;
}

const TEMPLATE_OPTIONS: {
  value: ReportTemplate;
  label: string;
  description: string;
  icon: React.ReactElement;
}[] = [
  {
    value: 'wellbeing_summary',
    label: 'Wellbeing Summary',
    description: 'Exec RAG summary + gender / age / department breakdowns',
    icon: <AssessmentIcon />,
  },
  {
    value: 'ai_insights',
    label: 'AI Insights & Action Plan',
    description: 'Highlights, recommendations, areas to watch + action plan',
    icon: <AutoAwesomeIcon />,
  },
  {
    value: 'weather_impact',
    label: 'Weather Impact',
    description: 'Wellbeing vs weather and anomaly callouts',
    icon: <CloudIcon />,
  },
  {
    value: 'individual_correlation',
    label: 'Individual & Correlation',
    description: 'Correlation matrix, scatter by segment, individual deep-dive',
    icon: <PersonIcon />,
  },
  {
    value: 'compare',
    label: 'Compare',
    description: 'Compare two metrics and one metric across departments',
    icon: <CompareArrowsIcon />,
  },
];

const DEFAULT_FILTERS: FilterState = {
  region: [],
  depot: [],
  department: [],
  jobRole: [],
  searchUser: [],
  ageRange: [],
  sex: [],
  shiftPattern: [],
  reliableDataOnly: false,
};

/** Default to the last full calendar month. */
const defaultRange = (): DateRange => {
  const end = dayjs().startOf('month').subtract(1, 'day');
  const start = end.startOf('month');
  return [start, end];
};

/**
 * Report generation wizard: pick period, audience filters (multi-select via the
 * shared FilterDrawer), and one or more report templates. Maps the shared
 * FilterState + DateRange into a ReportFilters and hands off to the generator.
 */
export const GenerateWizard: React.FC<GenerateWizardProps> = ({ open, onClose, onGenerate }) => {
  const { isCentralAdmin, clients, selectedClientId, setSelectedClientId, effectiveClientId } = useClientScope();
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange());
  // Optional comparison ("vs") period. Kept ephemeral (not persisted) so each
  // report starts without comparison - a report is a one-off artifact, unlike
  // the Wellness Overview dashboard which remembers the filter across visits.
  const [compareRange, setCompareRange] = useState<DateRange>([null, null]);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [templates, setTemplates] = useState<ReportTemplate[]>(['wellbeing_summary']);

  // The client selector drives the GLOBAL client scope so the FilterDrawer's
  // option lists (departments/regions/users) resolve for the chosen client.
  // Changing client clears any filters picked for the previous client.
  const handleClientChange = (next: string) => {
    setSelectedClientId(next);
    setFilters(DEFAULT_FILTERS);
  };

  const activeFilters = getAppliedFilterCount(filters);

  const toggleTemplate = (t: ReportTemplate) =>
    setTemplates((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const toIso = (d: Dayjs | null) => (d ? d.format('YYYY-MM-DD') : undefined);

  const [compareStart, compareEnd] = compareRange;
  // Toggle is on but no complete range picked yet: the comparison silently does
  // nothing, so warn the user instead of generating a non-comparison report.
  const compareIncomplete = compareEnabled && !(compareStart && compareEnd);

  // Only forward the comparison window once it is both enabled and fully picked.
  const comparing = compareEnabled && Boolean(compareStart && compareEnd);

  const buildFilters = (): ReportFilters => {
    const [start, end] = dateRange;
    return {
      start_date: toIso(start),
      end_date: toIso(end),
      compare_start_date: comparing ? toIso(compareStart) : undefined,
      compare_end_date: comparing ? toIso(compareEnd) : undefined,
      department: filters.department.length ? filters.department : undefined,
      age_range: filters.ageRange.length ? filters.ageRange : undefined,
      sex: filters.sex.length ? filters.sex[0] : undefined,
      shift_pattern: filters.shiftPattern.length ? filters.shiftPattern : undefined,
      region: filters.region.length ? filters.region : undefined,
      depot: filters.depot.length ? filters.depot : undefined,
      user_ids: filters.searchUser.length ? filters.searchUser : undefined,
    };
  };

  const formatRange = (range: DateRange): string => {
    const [start, end] = range;
    if (!start || !end) return '';
    // Same month -> "June 2026"; else "1 Jun - 30 Jun 2026".
    if (start.isSame(end, 'month')) return start.format('MMMM YYYY');
    return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
  };

  const periodLabel = (): string => formatRange(dateRange);

  const handleGenerate = () => {
    // Chosen client ("" = all clients → null) for central admins; non-central
    // admins are locked to their own effective client.
    const resolvedClientId = isCentralAdmin ? selectedClientId || null : effectiveClientId;
    onGenerate({
      templates,
      filters: buildFilters(),
      periodLabel: periodLabel(),
      clientId: resolvedClientId,
    });
  };

  return (
    <>
      <Dialog
        // Hide (don't unmount) the wizard while the filter drawer is open, so the
        // two modals never stack - otherwise the drawer's Autocomplete poppers
        // render behind the dialog and focus is trapped. State is preserved.
        open={open && !filterOpen}
        onClose={onClose}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Generate report</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {isCentralAdmin && (
              <Box>
                <Typography variant='subtitle2' gutterBottom>
                  Client
                </Typography>
                <FormControl fullWidth size='small'>
                  <InputLabel id='report-client-label'>Client</InputLabel>
                  <Select
                    labelId='report-client-label'
                    label='Client'
                    value={selectedClientId}
                    displayEmpty
                    onChange={(e) => handleClientChange(e.target.value)}
                  >
                    <MenuItem value=''>
                      <em>All clients</em>
                    </MenuItem>
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Reporting period
              </Typography>
              <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
                <DurationSelector value={dateRange} onChange={setDateRange} />
                {/* Optional comparison period, matching Wellness Overview. When enabled and
                    fully picked, the range is written to each generated block's
                    compare_start_date/compare_end_date and used as the change baseline. */}
                <CompareToControl
                  enabled={compareEnabled}
                  onEnabledChange={setCompareEnabled}
                  range={compareRange}
                  onRangeChange={setCompareRange}
                />
                <Typography variant='body2' color='text.secondary'>
                  {periodLabel()}
                  {comparing ? ` vs ${formatRange(compareRange)}` : ''}
                </Typography>
              </Stack>
              {compareIncomplete && (
                <Typography variant='caption' color='warning.main' sx={{ display: 'block', mt: 0.5 }}>
                  Pick a comparison period, or turn off "Compare to..." - no comparison is applied until a full range is
                  set.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Audience
              </Typography>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Button variant='outlined' onClick={() => setFilterOpen(true)}>
                  Filters
                </Button>
                {activeFilters > 0 ? (
                  <Chip
                    label={`${activeFilters} filter${activeFilters > 1 ? 's' : ''} applied`}
                    size='small'
                    color='primary'
                    onDelete={() => setFilters(DEFAULT_FILTERS)}
                  />
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    All participants
                  </Typography>
                )}
              </Stack>
              {filters.searchUser.length === 1 && (
                <Typography variant='caption' color='text.secondary'>
                  Single participant selected - individual report supported.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Report sections
              </Typography>
              <Stack spacing={1.5}>
                {TEMPLATE_OPTIONS.map((opt) => {
                  const selected = templates.includes(opt.value);
                  return (
                    <Card
                      key={opt.value}
                      variant='outlined'
                      sx={{
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'action.selected' : 'transparent',
                      }}
                    >
                      <CardActionArea onClick={() => toggleTemplate(opt.value)}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                          <Checkbox checked={selected} tabIndex={-1} sx={{ p: 0 }} />
                          <Box sx={{ color: 'primary.main', display: 'flex' }}>{opt.icon}</Box>
                          <Box>
                            <Typography fontWeight={600}>{opt.label}</Typography>
                            <Typography variant='body2' color='text.secondary'>
                              {opt.description}
                            </Typography>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color='inherit'>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleGenerate} disabled={templates.length === 0}>
            Generate report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sibling of the Dialog. The Dialog is hidden while this is open, so no
          modal stacking / z-index conflict. */}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => {
          setFilters(next);
          setFilterOpen(false);
        }}
        initialFilters={filters}
        dateRange={dateRange}
      />
    </>
  );
};
